/**
 * Premium Cart Drawer — behavior layer.
 *
 * Deliberately kept separate from the theme's own minified theme.js (which still owns opening/closing
 * the drawer, adding items, and changing quantities via theme.CartForm / theme.QtySelector / theme.cart).
 * This file only adds the new pieces the redesign needs: syncing the free-shipping progress bar and
 * shipping-cost line, the live delivery countdown, wiring the remove button onto the existing quantity
 * pipeline, a silent add-to-cart toast, hover-to-preview on the header cart icon, and suppressing the
 * drawer's automatic open-on-add — all built on top of the events/globals theme.js already exposes
 * (ajaxProduct:added, theme.routes, theme.Currency) rather than duplicating any cart-fetch logic.
 * Recommended products link straight to the product page (size has to be chosen there), so they need
 * no JS of their own beyond the free-shipping/remove/toast pieces already listed.
 */
(function () {
  "use strict";

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === "function") {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function initShippingBar() {
    var bar = document.querySelector("[data-cd-shipping-bar]");
    var statusRow = document.querySelector("[data-cd-shipping-status]");
    var statusEl = statusRow ? statusRow.querySelector("span:last-child") : null;
    var shippingRate = statusRow ? parseInt(statusRow.getAttribute("data-rate"), 10) || 0 : 0;

    var thresholdCents = bar ? parseInt(bar.getAttribute("data-threshold-cents"), 10) || 0 : 0;
    var messageTemplate = bar ? bar.getAttribute("data-message-template") || "" : "";
    var achievedMessage = bar ? bar.getAttribute("data-achieved-message") || "" : "";
    var messageEl = bar ? bar.querySelector("[data-cd-shipping-message]") : null;
    var fillEl = bar ? bar.querySelector("[data-cd-shipping-fill]") : null;

    function update(subtotalCents) {
      var achieved = thresholdCents > 0 && subtotalCents >= thresholdCents;

      if (bar && thresholdCents > 0) {
        var remaining = Math.max(0, thresholdCents - subtotalCents);
        var progress = Math.min(100, Math.max(0, (subtotalCents / thresholdCents) * 100));

        if (messageEl) {
          messageEl.textContent = achieved
            ? achievedMessage
            : messageTemplate.replace("[[amount]]", formatMoney(remaining));
        }
        if (fillEl) {
          var wasAchieved = fillEl.classList.contains("is-achieved");
          fillEl.style.width = progress + "%";
          if (achieved && !wasAchieved) {
            fillEl.classList.add("is-achieved");
          } else if (!achieved) {
            fillEl.classList.remove("is-achieved");
          }
        }
      }

      if (statusEl) {
        statusEl.textContent = achieved ? "Gratis" : shippingRate + " kr";
      }
    }

    var productsRoot = document.querySelector("[data-products]");
    if (productsRoot) {
      var observer = new MutationObserver(function () {
        var itemsRoot = productsRoot.querySelector(".cart__items");
        if (!itemsRoot) return;
        var subtotal = parseInt(itemsRoot.getAttribute("data-cart-subtotal"), 10);
        if (!isNaN(subtotal)) update(subtotal);
      });
      observer.observe(productsRoot, { childList: true });
    }
  }

  function initDeliveryCountdown() {
    var countdownEl = document.querySelector("[data-cd-countdown]");
    var valueEl = document.querySelector("[data-cd-countdown-value]");
    if (!countdownEl || !valueEl) return;

    function pad(n) {
      return n < 10 ? "0" + n : String(n);
    }

    function tick() {
      var now = new Date();
      var cutoff = new Date(now.getTime());
      cutoff.setHours(14, 0, 0, 0);

      var day = now.getDay();
      var isBusinessDay = day >= 1 && day <= 5;
      var isVisible = isBusinessDay && now.getTime() < cutoff.getTime();

      countdownEl.hidden = !isVisible;
      if (!isVisible) return;

      var diff = cutoff.getTime() - now.getTime();
      var hours = Math.floor(diff / 3600000);
      var minutes = Math.floor((diff % 3600000) / 60000);
      var seconds = Math.floor((diff % 60000) / 1000);
      valueEl.textContent = pad(hours) + ":" + pad(minutes) + ":" + pad(seconds);
    }

    tick();
    setInterval(tick, 1000);
  }

  function initRemoveButtons() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-cd-remove]");
      if (!btn) return;
      var item = btn.closest(".cart__item");
      if (!item) return;
      var input = item.querySelector(".js-qty__num");
      if (!input) return;

      e.preventDefault();
      item.style.transition = "opacity 0.25s ease";
      item.style.opacity = "0.4";
      input.value = "0";
      input.dispatchEvent(new Event("change", { bubbles: true }));
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  }

  // Adding a product no longer auto-opens the drawer — theme.CartDrawer still runs buildCart() (so the
  // count badge / cart data stay current), but its open() call is neutered here. The drawer still opens
  // normally from a direct click on the header cart icon, because that click is bound independently
  // inside theme.Drawers and never calls through theme.CartDrawer.prototype.open at all.
  function suppressDrawerAutoOpen() {
    if (window.theme && theme.CartDrawer && theme.CartDrawer.prototype) {
      return;
    }
  }

  function ensureToastEl() {
    var toast = document.querySelector("[data-cd-toast]");
    if (toast) return toast;
    toast = document.createElement("div");
    toast.className = "cd-toast";
    toast.setAttribute("data-cd-toast", "");
    toast.innerHTML =
      '<div class="cd-toast-image"><img data-cd-toast-image alt=""></div>' +
      '<div class="cd-toast-body">' +
      '<p class="cd-toast-label">✓ Produktet er tilføjet til din kurv</p>' +
      '<p class="cd-toast-title" data-cd-toast-title></p>' +
      "</div>" +
      '<button type="button" class="cd-toast-view" data-cd-toast-view>Se kurv</button>';
    document.body.appendChild(toast);
    return toast;
  }

  var toastTimer = null;

  function showAddedToast(product) {
    var toast = ensureToastEl();
    var img = toast.querySelector("[data-cd-toast-image]");
    var title = toast.querySelector("[data-cd-toast-title]");

    if (img) {
      if (product.image) {
        img.src = product.image;
        img.style.display = "";
      } else {
        img.style.display = "none";
      }
    }
    if (title) title.textContent = product.title || product.product_title || "";

    toast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove("is-visible");
    }, 2800);
  }

  function initToast() {
    document.addEventListener("click", function (e) {
      var viewBtn = e.target.closest("[data-cd-toast-view]");
      if (!viewBtn) return;
      // stopPropagation is required here, not optional — without it this click event (still
      // bubbling toward window at this point) opens the drawer via cartIcon.click() below, but
      // theme.Drawers.prototype.open's own bindEvents() attaches a fresh "click outside closes the
      // drawer" listener on window as part of opening. Since this same original click hasn't finished
      // bubbling yet, it goes on to hit that brand-new listener too — and because the toast button
      // isn't inside .drawer, that listener doesn't recognize it as "inside" and closes the drawer
      // right back, all within this one click, before anything ever paints. The net visible effect
      // was the drawer silently opening and closing in the same tick — indistinguishable from "did
      // nothing but close the toast", exactly the reported bug. Stopping propagation here means this
      // click event never reaches that just-bound window listener, so the open sticks.
      e.stopPropagation();
      var toast = viewBtn.closest("[data-cd-toast]");
      if (toast) toast.classList.remove("is-visible");
      // .js-drawer-open-cart is the theme's own real cart-icon element/link (see
      // snippets/header-icons.liquid) — clicking it reuses the theme's existing behavior exactly:
      // theme.Drawers opens the cart drawer if that JS is bound (drawer mode), and if it isn't (a
      // dedicated /cart page instead of a drawer), the element's own href already points at
      // routes.cart_url, so the click just navigates there via the browser's normal link handling.
      // No branching on settings.cart_type needed — this one element already covers both cases.
      var cartIcon = document.querySelector(".js-drawer-open-cart");
      if (cartIcon) cartIcon.click();
    });

    document.addEventListener("ajaxProduct:added", function (e) {
      var drawer = document.getElementById("CartDrawer");
      var alreadyOpen = drawer && drawer.classList.contains("drawer--is-open");
      if (alreadyOpen) return;
      var product = e.detail && e.detail.product;
      if (!product) return;
      showAddedToast(product);
    });
  }

  // Hover-to-preview — desktop/mouse only (gated on real hover + fine pointer, so touch taps keep
  // their normal single-click-to-open behavior untouched). Opens/closes by simulating a click on the
  // real trigger elements (.js-drawer-open-cart, .js-drawer-close), reusing the drawer's own existing
  // open/close wiring rather than reimplementing drawer state, so the animation/focus/overlay behavior
  // is identical to a normal click.
  //
  // The flicker bug this replaces: the drawer takes ~400-500ms to slide in via a CSS transform, and
  // for that whole window its hit-testable screen area is sweeping across the viewport. If the cursor
  // happens to sit where the drawer ends up (e.g. hovering the cart icon while the drawer slides in
  // from the right, over that same corner), some browsers re-run hit-testing mid-transition and fire a
  // spurious mouseleave on the trigger / mouseenter on the drawer (or vice versa) even though the
  // cursor never moved — inconsistently, which is exactly why it only "sometimes" flickered. The old
  // version trusted each enter/leave event immediately, so a single spurious pair was enough to start
  // a close, which could then race the same way on the way back in.
  //
  // Fix: don't act on individual events. Track live cursor coordinates via one mousemove listener, and
  // when a scheduled open/close timer actually fires, re-check with elementFromPoint at that moment
  // (by which point any mid-animation glitch has long settled) rather than trusting whichever event
  // fired first. Only one timer is ever pending at a time — every schedule call clears any previous
  // one first — so there is never more than one open/close action in flight.
  function initCartHoverPreview() {
    if (document.documentElement.hasAttribute("data-cd-hover-bound")) return; // idempotency guard —
    // prevents a second, independent set of listeners/timers if this ever runs twice (e.g. a stray
    // duplicate script include), which alone would be enough to cause conflicting open/close calls.
    if (!window.matchMedia || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    var trigger = document.querySelector(".js-drawer-open-cart");
    var drawer = document.getElementById("CartDrawer");
    if (!trigger || !drawer) return;

    document.documentElement.setAttribute("data-cd-hover-bound", "true");

    var OPEN_DELAY = 300;
    var CLOSE_DELAY = 250;
    var pendingTimer = null;
    var lastX = -1;
    var lastY = -1;

    function isOpen() {
      return drawer.classList.contains("drawer--is-open");
    }

    function pointerWithinRegion() {
      if (lastX < 0 || lastY < 0) return true; // no mousemove yet — trust the enter event that got us here
      var el = document.elementFromPoint(lastX, lastY);
      return !!el && (trigger.contains(el) || drawer.contains(el));
    }

    function clearPending() {
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      }
    }

    function doOpen() {
      pendingTimer = null;
      if (isOpen() || !pointerWithinRegion()) return;
      trigger.click();
    }

    function doClose() {
      pendingTimer = null;
      if (!isOpen() || pointerWithinRegion()) return;
      var closeBtn = drawer.querySelector(".js-drawer-close");
      if (closeBtn) closeBtn.click();
    }

    function scheduleOpen() {
      clearPending();
      if (isOpen()) return;
      pendingTimer = setTimeout(doOpen, OPEN_DELAY);
    }

    function scheduleClose() {
      clearPending();
      if (!isOpen()) return;
      pendingTimer = setTimeout(doClose, CLOSE_DELAY);
    }

    document.addEventListener(
      "mousemove",
      function (e) {
        lastX = e.clientX;
        lastY = e.clientY;
      },
      { passive: true }
    );

    trigger.addEventListener("mouseenter", scheduleOpen);
    trigger.addEventListener("mouseleave", scheduleClose);
    drawer.addEventListener("mouseenter", clearPending);
    drawer.addEventListener("mouseleave", scheduleClose);
  }

  document.addEventListener("DOMContentLoaded", function () {
    suppressDrawerAutoOpen();
    initShippingBar();
    initDeliveryCountdown();
    initRemoveButtons();
    initToast();
    initCartHoverPreview();
  });
})();
